import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { formatCompactNumber, formatPercent } from '../charts/formatters'
import { resolvePublicAssetUrl, socialIcon } from '../../lib/assets'
import ChartPanel from '../charts/ChartPanel'
import ChartContainer from '../charts/ChartContainer'
import './PlatformPerformanceComparison.css'

const ENGAGEMENT_RATE_BAR_COLOR = '#111827'

const PLATFORM_CONFIG = {
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    icon: socialIcon('instagram.svg'),
    colorVar: '--dashboard-platform-instagram',
  },
  facebook: {
    id: 'facebook',
    label: 'Facebook',
    icon: socialIcon('facebook.svg'),
    colorVar: '--dashboard-platform-facebook',
  },
  linkedin: {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: socialIcon('linkedin.svg'),
    colorVar: '--dashboard-platform-linkedin',
  },
  twitter: {
    id: 'twitter',
    label: 'Twitter/X',
    icon: socialIcon('x.png'),
    colorVar: '--dashboard-platform-twitter',
  },
  tiktok: {
    id: 'tiktok',
    label: 'TikTok',
    icon: socialIcon('tiktok.svg'),
    colorVar: '--dashboard-platform-tiktok-primary',
  },
}

function BarWithLogo({ x, y, width, height, payload }) {
  const config = payload?.config
  const icon = config?.icon ? resolvePublicAssetUrl(config.icon) : null
  const barWidth = Math.max(0, Number(width) || 0)
  const logoSize = Math.min(16, Math.max(12, height * 0.9))
  const tipOffset = 6

  return (
    <g>
      {barWidth > 0 && (
        <rect
          x={x}
          y={y}
          width={barWidth}
          height={height}
          rx={2}
          ry={2}
          fill={ENGAGEMENT_RATE_BAR_COLOR}
        />
      )}
      {icon && barWidth > logoSize && (
        <image
          href={icon}
          xlinkHref={icon}
          x={x + barWidth + tipOffset}
          y={y + (height - logoSize) / 2}
          width={logoSize}
          height={logoSize}
          preserveAspectRatio="xMidYMid meet"
        />
      )}
    </g>
  )
}

function ThinLifetimeBar({ x, y, width, height }) {
  const barWidth = Math.max(0, Number(width) || 0)
  if (barWidth <= 0) return null
  const barHeight = Math.max(3, Math.min(4, height))
  return (
    <rect
      x={x}
      y={y + (height - barHeight) / 2}
      width={barWidth}
      height={barHeight}
      rx={1}
      ry={1}
      fill="#3b82f6"
      opacity={0.55}
    />
  )
}

function YAxisTickWithLogo({ x, y, payload }) {
  const platformId = payload?.value
  const config = platformId ? PLATFORM_CONFIG[platformId] : null
  const label = config?.label || platformId

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={-4} y={4} textAnchor="end" fontSize={11} fill="#6b7280">
        {label}
      </text>
    </g>
  )
}

const PlatformPerformanceComparison = ({
  apiChartData = null,
  loading = false,
}) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(apiChartData)) return []

    return apiChartData.map((row) => {
      const config = PLATFORM_CONFIG[row.platform] || {
        id: row.platform,
        label: row.platform,
        icon: null,
        colorVar: '--chart-1',
      }

      return {
        ...row,
        platformLabel: config.label,
        engagementRate: Math.max(0, Number(row.engagementRate) || 0),
        lifetimeEngagements: Math.max(0, Number(row.lifetimeEngagements) || 0),
        config,
      }
    })
  }, [apiChartData])

  const renderCustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    const row = payload[0].payload
    const platformLabel = row?.platformLabel || chartData.find((r) => r.platform === label)?.platformLabel || label

    return (
      <div className="rg-chart__tooltip rg-chart__tooltip--platformPerformance">
        <div className="rg-chart__tooltip-title">{platformLabel}</div>
        <div className="rg-chart__tooltip-row ppc-tooltip-engagement-row">
          <span>
            <span
              className="rg-chart__tooltip-dot"
              style={{ backgroundColor: ENGAGEMENT_RATE_BAR_COLOR }}
            />
            Engagement Rate
          </span>
          <span className="ppc-tooltip-engagement-value">
            <strong>{formatPercent(row.engagementRate, 1)}</strong>
          </span>
        </div>
        <div className="rg-chart__tooltip-row ppc-tooltip-engagement-row">
          <span>
            <span className="rg-chart__tooltip-dot" style={{ backgroundColor: 'var(--chart-2)' }} />
            Lifetime Engagement
          </span>
          <span className="ppc-tooltip-engagement-value">
            <strong>{formatCompactNumber(row.lifetimeEngagements || 0)}</strong>
          </span>
        </div>
      </div>
    )
  }

  const empty = chartData.length === 0

  return (
    <ChartPanel
      dataChartId="platformPerformanceComparison"
      title="Platform Performance Comparison"
      subtitle="Compare engagement rate and lifetime engagement across connected platforms."
      loading={loading}
      loadingMessage="Loading platform performance…"
      className="platformPerformanceComparisonPanel"
    >
      <div className="platformPerformanceComparison platformPerformanceComparison--compact">
        <div className="audienceInsightPanel">
          <div className="audienceInsightChart">
            <ChartContainer
              height={280}
              forceRender
              empty={empty}
              emptyMessage="Connect platforms to see performance comparison."
            >
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 28, right: 56, left: 8, bottom: 8 }}
                barGap={8}
                barCategoryGap="22%"
              >
                <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  xAxisId="rate"
                  type="number"
                  orientation="bottom"
                  stroke="var(--chart-muted)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--chart-border)' }}
                  width={40}
                  domain={[0, 'auto']}
                  allowDataOverflow={false}
                  tickFormatter={(v) => formatPercent(v, 1)}
                />
                <XAxis
                  xAxisId="lifetime"
                  type="number"
                  orientation="top"
                  stroke="var(--chart-muted)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--chart-border)' }}
                  width={40}
                  domain={[0, 'auto']}
                  allowDataOverflow={false}
                  tickFormatter={formatCompactNumber}
                />
                <YAxis
                  type="category"
                  dataKey="platform"
                  stroke="var(--chart-muted)"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: 'var(--chart-border)' }}
                  width={80}
                  tick={<YAxisTickWithLogo />}
                />
                <Tooltip content={renderCustomTooltip} />
                <Bar
                  xAxisId="rate"
                  dataKey="engagementRate"
                  name="Engagement Rate"
                  barSize={11}
                  shape={(props) => <BarWithLogo {...props} />}
                />
                <Bar
                  xAxisId="lifetime"
                  dataKey="lifetimeEngagements"
                  name="Lifetime Engagement"
                  barSize={4}
                  shape={(props) => <ThinLifetimeBar {...props} />}
                />
              </BarChart>
            </ChartContainer>
            {!empty && (
              <div className="campaignPerformanceLegend">
                <div className="campaignPerformanceLegendItem">
                  <span className="campaignPerformanceLegendSwatch platformPerformanceComparisonLegendSwatch--engagementRate" />
                  <span>Engagement Rate</span>
                </div>
                <div className="campaignPerformanceLegendItem">
                  <span className="campaignPerformanceLegendSwatch platformPerformanceComparisonLegendSwatch--lifetime" />
                  <span>Lifetime Engagement</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ChartPanel>
  )
}

export default PlatformPerformanceComparison
